import type { BudgetLineRow } from "@/hooks/use-project-detail";
import type { ProjectPhase } from "@/lib/offer-types";
import type { BudgetLineSnapshot } from "@/lib/budget-revision";
import type { BudgetLineInsert, BudgetLineUpdate } from "@/hooks/use-budget-line-mutations";
import {
  planAssignmentAdjustments,
  type RevisionAssignmentAdjustment,
} from "@/lib/budget-revision-assignments";

/**
 * VIB-1202: lokal draft-modell for revisjonsmodus + ren omgjøring til
 * commit-endringer (updates/creates/deletes). Holdt UI-fri og testbar.
 */
/** Avstand mellom sort_order-verdier, samme steg som offer_lines bruker. */
export const SORT_ORDER_STEP = 10;

export interface RevisionDraftLine {
  /** Stabil React-nøkkel (id for eksisterende, "new-…" for nye). */
  key: string;
  /** DB-id, eller null for en ny linje som ennå ikke er persistert. */
  id: string | null;
  isNew: boolean;
  deleted: boolean;
  description: string;
  phase: ProjectPhase;
  cost_type: "internal" | "external";
  quantity: number | null;
  quantity_hours: number | null;
  amount: number;
  estimated_cost: number;
  template_id: string | null;
}

export function toDraft(bl: BudgetLineRow): RevisionDraftLine {
  return {
    key: bl.id,
    id: bl.id,
    isNew: false,
    deleted: false,
    description: bl.description ?? "",
    phase: (bl.phase as ProjectPhase) ?? "production",
    cost_type: (bl.cost_type as "internal" | "external") ?? "internal",
    quantity: bl.quantity ?? null,
    quantity_hours: bl.quantity_hours ?? null,
    amount: bl.amount ?? 0,
    estimated_cost: bl.estimated_cost ?? 0,
    template_id: bl.template_id ?? null,
  };
}

export function draftToSnapshot(d: RevisionDraftLine): BudgetLineSnapshot {
  return {
    id: d.id ?? "",
    description: d.description,
    phase: d.phase,
    cost_type: d.cost_type,
    quantity: d.quantity,
    quantity_hours: d.quantity_hours,
    amount: d.amount,
    estimated_cost: d.estimated_cost,
  };
}

/** Linje-objekt calcLiveBfEstimated kan lese (BF-preview). */
export function draftToBfLine(d: RevisionDraftLine) {
  return {
    upsell_id: null as string | null,
    phase: d.phase,
    description: d.description,
    cost_type: d.cost_type,
    quantity_hours: d.quantity_hours,
    amount: d.amount,
    estimated_cost: d.estimated_cost,
  };
}

export interface RevisionCommitUpdate {
  id: string;
  patch: BudgetLineUpdate;
  before: BudgetLineSnapshot;
  after: BudgetLineSnapshot;
}

export interface RevisionCommitDelete {
  id: string;
  before: BudgetLineSnapshot;
}

export interface RevisionCommitChanges {
  updates: RevisionCommitUpdate[];
  creates: BudgetLineInsert[];
  deletes: RevisionCommitDelete[];
  assignmentAdjustments: RevisionAssignmentAdjustment[];
}

/** Er en eksisterende draft-linje endret fra sin opprinnelige rad? */
function isChanged(orig: BudgetLineRow, d: RevisionDraftLine): boolean {
  return (
    (orig.description ?? "") !== d.description ||
    (orig.phase ?? "") !== d.phase ||
    (orig.cost_type ?? "") !== d.cost_type ||
    (orig.quantity ?? null) !== d.quantity ||
    (orig.quantity_hours ?? null) !== d.quantity_hours ||
    (orig.amount ?? 0) !== d.amount ||
    (orig.estimated_cost ?? 0) !== d.estimated_cost
  );
}

/**
 * Omgjør drafts + de opprinnelige radene til commit-endringene.
 * - Endrede eksisterende linjer (ikke slettet) → updates.
 * - Nye linjer (ikke slettet) → creates.
 * - Eksisterende linjer markert slettet → deletes.
 */
export function buildRevisionChanges(
  drafts: RevisionDraftLine[],
  originalById: Map<string, BudgetLineRow>,
  projectId: string,
): RevisionCommitChanges {
  const updates: RevisionCommitUpdate[] = [];
  const creates: BudgetLineInsert[] = [];
  const deletes: RevisionCommitDelete[] = [];
  const assignmentAdjustments: RevisionAssignmentAdjustment[] = [];

  // VIB-2866: nye linjer settes inn i én batch og får identisk created_at,
  // så de må bære sin egen rekkefølge. De legges etter fasens siste
  // eksisterende linje, i draft-rekkefølge.
  const nextSortOrderByPhase = new Map<string, number>();
  for (const orig of originalById.values()) {
    const phase = orig.phase ?? "";
    const current = nextSortOrderByPhase.get(phase) ?? 0;
    nextSortOrderByPhase.set(phase, Math.max(current, (orig.sort_order ?? 0) + SORT_ORDER_STEP));
  }

  for (const d of drafts) {
    if (d.isNew) {
      if (d.deleted) continue;
      const sortOrder = nextSortOrderByPhase.get(d.phase) ?? SORT_ORDER_STEP;
      nextSortOrderByPhase.set(d.phase, sortOrder + SORT_ORDER_STEP);
      creates.push({
        project_id: projectId,
        phase: d.phase,
        description: d.description,
        amount: d.amount,
        cost_type: d.cost_type,
        quantity: d.quantity,
        quantity_hours: d.quantity_hours,
        estimated_cost: d.estimated_cost,
        template_id: d.template_id,
        sort_order: sortOrder,
      });
      continue;
    }

    if (!d.id) continue;
    const orig = originalById.get(d.id);
    if (!orig) continue;

    if (d.deleted) {
      deletes.push({ id: d.id, before: draftToSnapshot(d) });
      continue;
    }

    if (isChanged(orig, d)) {
      updates.push({
        id: d.id,
        patch: {
          description: d.description,
          phase: d.phase,
          cost_type: d.cost_type,
          quantity: d.quantity,
          quantity_hours: d.quantity_hours,
          amount: d.amount,
          estimated_cost: d.estimated_cost,
        },
        before: draftToSnapshot(toDraft(orig)),
        after: draftToSnapshot(d),
      });

      const targetHours = d.cost_type === "internal" ? Number(d.quantity_hours ?? 0) : 0;
      assignmentAdjustments.push(
        ...planAssignmentAdjustments(
          d.id,
          orig.assignments.map((assignment) => ({
            id: assignment.id,
            userId: assignment.user_id,
            userName: assignment.user?.full_name ?? "Ukjent",
            hours: Number(assignment.hours ?? 0),
            taskStatus: assignment.task_status,
          })),
          targetHours,
          d.description || orig.description || "Oppgave",
        ),
      );
    }
  }

  return { updates, creates, deletes, assignmentAdjustments };
}
